import {Component, OnDestroy, OnInit} from '@angular/core';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {IntercomService} from '../../../services/intercom.service';
import {ToastrService} from 'ngx-toastr';
import {ApplyCouponResponse} from '../../../models/SubscriptionsModels';
import {EMPTY} from 'rxjs';
import {catchError, finalize, take, tap} from 'rxjs/operators';
import {MixpanelService} from '../../../services/mixpanel.service';

export enum DowngradeModalVariant {
    Before = 'before',
    FreeYear = 'freeYear',
    FreeYearSuccess = 'freeYearSuccess',
    Done = 'done'
}

export interface DowngradeModalActions {
    onDowngrade?: () => import('rxjs').Observable<string> | void;
    continueFree?: () => void;
    keepPlanWithCoupon?: () => import('rxjs').Observable<ApplyCouponResponse> | void;
}

export interface DowngradeModalData {
    variant: DowngradeModalVariant;
    cancelAtDate?: string;
    onTrial?: boolean;
    actions?: DowngradeModalActions;
}

interface OfferCopy {
    headline: string;
    body: string;
    cta: string;
    successHeadline: string;
}

@Component({
    selector: 'app-downgrade-modal',
    template: `
    <div class="flex flex-column justify-content-center align-items-center w-full" [ngSwitch]="variant">

      <!-- BEFORE YOU DOWNGRADE -->
      <ng-container *ngSwitchCase="DowngradeModalVariant.Before">
        <img appResponsiveImage="modals/Downgrade%20Modal" alt="Text Alert" class="max-h-14rem"/>
        <h4 class="text-center font-bold my-2">Before you downgrade</h4>

        <div class="px-6" style="line-height: 1.7">
          <div class="text text-left mb-3">
            Talk to support to make sure your membership is set up for the best value before making changes.
          </div>

          <div class="text text-left">Some common issues that would affect the quality of flight deals:</div>
          <ul class="px-5 my-1">
            <li>Have you set up SMS alerts?</li>
            <li>Have you selected at least one major international departure?</li>
            <li>Have you added jesse&#64;dollarflightclub.com to your safe-senders list?</li>
          </ul>

          <div class="p-fluid grid mt-4">
            <div class="col-12 py-2">
              <button pButton label="Contact Support" class="learn-btn w-full" severity="info"
                      (click)="contactSupport()"></button>
            </div>
          </div>
        </div>

        <span class="text-center mb-3 underline cursor-pointer" (click)="goTo(DowngradeModalVariant.FreeYear)">
          Downgrade
        </span>
      </ng-container>

      <!-- OFFER (60% off if on trial, otherwise 1 year free) -->
      <ng-container *ngSwitchCase="DowngradeModalVariant.FreeYear">
        <img appResponsiveImage="modals/Downgrade%20Modal" alt="Text Alert" class="max-h-14rem"/>

        <div class="px-7" style="line-height: 1.7">
          <h4 class="text-center font-bold my-2">{{ offer.headline }}</h4>

          <div class="text text-left mb-3">
            {{ offer.body }}
          </div>

          <div class="p-fluid grid mt-4">
            <div class="col-12 py-2">
              <button pButton class="learn-btn w-full p-button-label" severity="info" (click)="applyOffer()">
                <span *ngIf="loading" class="m-auto pi pi-spinner pi-spin"></span>
                <span *ngIf="!loading" class="m-auto">{{ offer.cta }}</span>
              </button>
            </div>
          </div>
        </div>

        <span class="text-center mb-3 underline cursor-pointer" (click)="downgrade()">
          Downgrade
        </span>
      </ng-container>

      <!-- OFFER SUCCESS -->
      <ng-container *ngSwitchCase="DowngradeModalVariant.FreeYearSuccess">
        <img appResponsiveImage="modals/Downgrade%20Modal" alt="Text Alert" class="max-h-14rem"/>
        <h4 class="text-center font-bold my-3 px-2 text-3xl">{{ offer.successHeadline }}</h4>

        <div class="px-5" style="line-height: 1.7">
          <div class="text text-left mb-3">
            The DFC team appreciates that you're willing to give us another chance.
          </div>
          <div class="text text-left">
            Would you mind filling out a short survey? We value your opinion and want to improve the experience for
            future customers.
          </div>

          <div class="p-fluid grid mt-4 mx-auto">
            <div class="col-12 px-0 py-2">
              <button pButton label="Share Feedback" severity="info" class="learn-btn w-full"
                      (click)="onFeedback()"></button>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- DOWNGRADED SUCCESS -->
      <ng-container *ngSwitchCase="DowngradeModalVariant.Done">
        <img appResponsiveImage="modals/Downgrade Hero" alt="Done" class="w-11 md:max-h-19rem"/>
        <h4 class="text-center font-bold my-3 px-2 md:text-2xl">Your plan has been downgraded</h4>

        <div class="px-5 mb-2" style="line-height: 1.7">
            <div *ngIf="data.cancelAtDate">Your plan is set to cancel on <strong>{{ data.cancelAtDate }}</strong></div>

          <div class="text mb-2 text-left">
            The DFC team appreciates that you gave us a chance even if it didn't work out.
          </div>

          <div class="text-left">
            Would you mind filling out a short survey? We value your opinion and want to improve the experience for
            future customers.
          </div>

          <div class="p-fluid grid mt-4 mx-auto">
            <div class="col-12 px-0 py-2">
              <button pButton label="Share Feedback" severity="info" class="learn-btn w-full"
                      (click)="onFeedback()"></button>
            </div>
          </div>
        </div>
      </ng-container>

    </div>
  `,
    styles: [`
    .cursor-pointer { cursor: pointer; }
  `]
})
export class DowngradeModalComponent implements OnInit, OnDestroy {
    variant!: DowngradeModalVariant;
    loading = false;

    offer: OfferCopy = {
        headline: '',
        body: '',
        cta: '',
        successHeadline: ''
    };
    protected readonly DowngradeModalVariant = DowngradeModalVariant;

    get data(): DowngradeModalData {
        return (this.config?.data ?? {}) as DowngradeModalData;
    }

    constructor(
        public config: DynamicDialogConfig,
        private ref: DynamicDialogRef,
        private intercom: IntercomService,
        private toast: ToastrService,
        private mixpanel: MixpanelService
    ) {}

    ngOnInit() {
        this.variant = this.data.variant;

        const onTrial = !!this.data?.onTrial;

        this.offer = onTrial
            ? {
                headline: 'Enjoy 60% off your next year on us',
                body: `Share quick feedback with us why you're considering downgrading and we'll give you 60% off your next year as we use your feedback to improve.`,
                cta: 'Get 60% Off',
                successHeadline: `You've redeemed 60% off!`
            }
            : {
                headline: 'Enjoy a free year on us before you decide',
                body: `Share quick feedback with us why you're considering downgrading and we'll give you a free year as we use your feedback to improve.`,
                cta: 'Get 1 Year Free',
                successHeadline: `You've redeemed a free year!`
            };
    }

    goTo(next: DowngradeModalVariant) {
        this.variant = next;
    }

    contactSupport(): void {
        this.intercom.openMessenger();
    }

    downgrade(): void {
        const op = this.data.actions?.onDowngrade?.();
        if (!op) return;

        this.loading = true;

        op.pipe(
            take(1),
            tap((cancelDate: string) => this.finishDowngrade(cancelDate)),
            catchError((e: any) => {
                this.toast.error(e.error.message, 'Error while downgrading');
                return EMPTY;
            }),
            finalize(() => (this.loading = false))
        ).subscribe();
    }

    private finishDowngrade(cancelDate?: string) {
        if (cancelDate) {
            this.data.cancelAtDate = cancelDate;
        }
        this.variant = DowngradeModalVariant.Done;
    }

    applyOffer() {
        const op$ = this.data.actions?.keepPlanWithCoupon?.();
        if (!op$) {
            return;
        }

        this.loading = true;

        op$.pipe(
            take(1),
            tap(() => (this.variant = DowngradeModalVariant.FreeYearSuccess)),
            catchError((e: any) => {
                this.toast.error(e.error.message, 'Error while applying offer');
                return EMPTY;
            }),
            finalize(() => (this.loading = false))
        ).subscribe();
    }

    onFeedback(): void {
        this.mixpanel.track('Share feedback button clicked')
        window.open('https://form.typeform.com/to/FSvG5VC3', '_blank');
        this.data.actions?.continueFree?.();
        this.ref.close({status: this.variant});
    }

    close(): void {
        this.mixpanel.track('Downgrade modal closed')
        this.ref.close({status: this.variant});
    }

    ngOnDestroy() {
        if ([DowngradeModalVariant.Done, DowngradeModalVariant.FreeYearSuccess].includes(this.variant)) {
            this.data.actions?.continueFree?.();
        }
    }
}
